import { ReactNode, ChangeEvent } from 'react';
import clsx from 'clsx';
import Checkbox, { CheckboxProps } from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useStyles } from './check-box.style';

interface IOnChangeHandler {
    (e: ChangeEvent<HTMLInputElement>): void;
}

type CheckBoxProps = {
    /**
     * The html label of the field
     */
    label?: string;
    /**
     * If true, the input element will be required.
     */
    required?: boolean;
    /**
     * If true, the input element will be disabled.
     */
    disabled?: boolean;
    /**
     * You can add children as label too. Use this if you label needs to contain html or other react components.
     */
    children?: ReactNode;
    /**
     * The value of the input
     */
    value?: string;
    /**
     * Controls the "check" html attribute
     */
    checked?: boolean;
    /**
     * Callback function fired when the checkbox is changed
     */
    onChange?: IOnChangeHandler;
    /**
     * Controls the "name" html attribute
     */
    name?: string;
    /**
     * Changes the color of the checkbox
     */
    color?: 'primary' | 'secondary' | 'default';
    /**
     * Triggers the error state of the field
     */
    error?: boolean | undefined;
} & CheckboxProps;

const CheckBox = ({
    label,
    required = false,
    disabled = false,
    value,
    children,
    checked,
    onChange,
    name,
    error = false,
    ...rest
}: CheckBoxProps) => {
    const classes = useStyles();
    return (
        <FormControlLabel
            className={error ? classes.error : ''}
            control={
                <Checkbox
                    className={clsx(classes.root, error ? classes.errorBorder : '')}
                    disableRipple
                    checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
                    icon={<span className={clsx(classes.icon, error ? classes.errorBorder : '')} />}
                    checked={checked}
                    indeterminate={false}
                    color="default"
                    required={required}
                    disabled={disabled && disabled}
                    value={value}
                    onChange={onChange}
                    name={name}
                    id={`${rest.id}-inner`}
                    {...rest}
                />
            }
            label={children || label}
        />
    );
};

export { CheckBox };
export type { CheckBoxProps };
export type { IOnChangeHandler };
